import React, { useState, useRef, useLayoutEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';

const ITEM_GAP = 20;
const RESERVED_SPACE = 60;

const useVisibleCount = (summary) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const keys = useMemo(() => Object.keys(summary), [summary]);
  const [visibleCount, setVisibleCount] = useState(keys.length);

  const recalcVisibleCount = React.useMemo(() => debounce(() => {
    if (typeof window === 'undefined') return;

    const container = containerRef.current;
    const measureContainer = measureRef.current;
    if (!container || !measureContainer) return;

    const containerWidth = container.offsetWidth;

    const reservedWidth = containerWidth * 0.2;
    const children = Array.from(measureContainer.children);

    let currentWidth = 0;
    let newVisibleCount = 0;


    for (let i = 0; i < children.length; i++) {
      currentWidth += children[i].offsetWidth + ITEM_GAP;
    }

    if (currentWidth < containerWidth - RESERVED_SPACE) {
      setVisibleCount(keys.length);
      return;
    }

    currentWidth = 0;
    for (let i = 0; i < children.length; i++) {
      const itemWidth = children[i].offsetWidth + ITEM_GAP;
      if (currentWidth + itemWidth < containerWidth - reservedWidth) {
        currentWidth += itemWidth;
        newVisibleCount++;
      } else {
        break;
      }
    }

    setVisibleCount(newVisibleCount);
  }, 150), [keys.length]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const resizeObserver = new ResizeObserver(recalcVisibleCount);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    recalcVisibleCount();
    window.addEventListener('resize', recalcVisibleCount);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', recalcVisibleCount);
      recalcVisibleCount.cancel();
    };
  }, [recalcVisibleCount]);

  return { visibleCount, containerRef, measureRef };
};

export default useVisibleCount;
